import { fallbackPosts } from "../data/fallbackPosts";
import type { Post, PostFormValues } from "../types/post";
import {
  applyCategoryToTags,
  getCategorizedPosts,
  getPostCategory,
  hasPostCategory,
  normalizeCategory,
  stripPageTags
} from "./categories";
import { isSupabaseConfigured, supabase } from "./supabase";

const table = "posts";

type PostSavePayload = Omit<PostFormValues, "category"> & {
  category?: string | null;
};

function byRecentPublished(a: Post, b: Post): number {
  const left = a.published_at ?? a.created_at;
  const right = b.published_at ?? b.created_at;

  return new Date(right).getTime() - new Date(left).getTime();
}

function estimateMinutes(content: string, language: "ar" | "en"): number {
  const plain = content
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/[#>*_`~\-[\]()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const words = plain ? plain.split(/\s+/).length : 0;
  const wordsPerMinute = language === "ar" ? 180 : 225;

  return Math.max(1, Math.ceil(words / wordsPerMinute));
}

function isMissingCategoryColumnError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  const maybeError = error as { code?: string; message?: string };
  const message = maybeError.message ?? "";
  return (
    maybeError.code === "PGRST204" ||
    /column .*category|category.* column|schema cache/i.test(message)
  );
}

function normalizePostValues(
  values: PostFormValues,
  includeCategoryColumn = true
): PostSavePayload {
  const reading_time_minutes =
    values.reading_time_minutes > 0
      ? values.reading_time_minutes
      : estimateMinutes(values.content, values.language);
  const category = normalizeCategory(values.category);
  const normalizedValues = {
    ...values,
    category,
    tags: applyCategoryToTags(values.tags, category),
    reading_time_minutes
  };
  const withPublishedAt =
    values.status === "published" && !values.published_at
      ? { ...normalizedValues, published_at: new Date().toISOString() }
      : values.status !== "published"
        ? { ...normalizedValues, published_at: null }
        : normalizedValues;

  if (!includeCategoryColumn) {
    const payloadWithoutCategory: PostSavePayload = { ...withPublishedAt };
    delete payloadWithoutCategory.category;
    return payloadWithoutCategory;
  }

  return withPublishedAt;
}

export function toPostFormValues(post: Post): PostFormValues {
  return {
    title: post.title,
    subtitle: post.subtitle,
    slug: post.slug,
    excerpt: post.excerpt,
    content: post.content,
    content_format: post.content_format,
    language: post.language,
    direction: post.direction,
    status: post.status,
    visibility: post.visibility,
    content_type: post.content_type,
    category: getPostCategory(post),
    cover_image_url: post.cover_image_url,
    tags: stripPageTags(post.tags),
    font_family: post.font_family,
    reader_theme: post.reader_theme,
    reading_time_minutes: post.reading_time_minutes,
    is_featured: post.is_featured,
    published_at: post.published_at
  };
}

export async function getPublishedPosts(): Promise<Post[]> {
  if (!isSupabaseConfigured || !supabase) {
    return getCategorizedPosts(
      fallbackPosts.filter(
        (post) => post.status === "published" && post.visibility === "public"
      )
    )
      .sort(byRecentPublished);
  }

  const { data, error } = await supabase
    .from(table)
    .select("*")
    .eq("status", "published")
    .eq("visibility", "public")
    .order("published_at", { ascending: false });

  if (error) {
    throw error;
  }

  return getCategorizedPosts((data ?? []) as Post[]);
}

export async function getPublishedPostBySlug(slug: string): Promise<Post | null> {
  if (!isSupabaseConfigured || !supabase) {
    const post =
      fallbackPosts.find(
        (post) =>
          post.slug === slug &&
          post.status === "published" &&
          post.visibility === "public"
      ) ?? null;

    return post && hasPostCategory(post) ? post : null;
  }

  const { data, error } = await supabase
    .from(table)
    .select("*")
    .eq("slug", slug)
    .eq("status", "published")
    .eq("visibility", "public")
    .maybeSingle();

  if (error) {
    throw error;
  }

  const post = (data as Post | null) ?? null;
  return post && hasPostCategory(post) ? post : null;
}

export async function getAllPosts(): Promise<Post[]> {
  if (!isSupabaseConfigured || !supabase) {
    return [...fallbackPosts].sort(byRecentPublished);
  }

  const { data, error } = await supabase
    .from(table)
    .select("*")
    .order("updated_at", { ascending: false });

  if (error) {
    throw error;
  }

  return (data ?? []) as Post[];
}

export async function getPostById(id: string): Promise<Post | null> {
  if (!isSupabaseConfigured || !supabase) {
    return fallbackPosts.find((post) => post.id === id) ?? null;
  }

  const { data, error } = await supabase
    .from(table)
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return (data as Post | null) ?? null;
}

export async function createPost(values: PostFormValues): Promise<Post> {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error("Supabase is not configured yet.");
  }

  const { data, error } = await supabase
    .from(table)
    .insert(normalizePostValues(values))
    .select("*")
    .single();

  if (error) {
    if (isMissingCategoryColumnError(error)) {
      const retry = await supabase
        .from(table)
        .insert(normalizePostValues(values, false))
        .select("*")
        .single();

      if (retry.error) {
        throw retry.error;
      }

      return retry.data as Post;
    }

    throw error;
  }

  return data as Post;
}

export async function updatePost(id: string, values: PostFormValues): Promise<Post> {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error("Supabase is not configured yet.");
  }

  const { data, error } = await supabase
    .from(table)
    .update(normalizePostValues(values))
    .eq("id", id)
    .select("*")
    .single();

  if (error) {
    if (isMissingCategoryColumnError(error)) {
      const retry = await supabase
        .from(table)
        .update(normalizePostValues(values, false))
        .eq("id", id)
        .select("*")
        .single();

      if (retry.error) {
        throw retry.error;
      }

      return retry.data as Post;
    }

    throw error;
  }

  return data as Post;
}

export async function deletePost(id: string): Promise<void> {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error("Supabase is not configured yet.");
  }

  const { error } = await supabase.from(table).delete().eq("id", id);

  if (error) {
    throw error;
  }
}

export async function uploadCoverImage(file: File): Promise<string> {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error("Supabase is not configured yet.");
  }

  const extension = file.name.split(".").pop() ?? "jpg";
  const filename = `${crypto.randomUUID()}.${extension.toLowerCase()}`;
  const path = `covers/${new Date().getFullYear()}/${filename}`;

  const { error } = await supabase.storage
    .from("post-covers")
    .upload(path, file, {
      cacheControl: "3600",
      upsert: false
    });

  if (error) {
    throw error;
  }

  const { data } = supabase.storage.from("post-covers").getPublicUrl(path);
  return data.publicUrl;
}
